import React, { useRef, useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link as RouterLink } from "react-router-dom";
import { PRICING, PROBLEMS, PROCESS, STATS, TESTIMONIALS, FAQS, TRUST_LOGOS, SERVICES } from "../mock/mock";
import { BLOG_POSTS } from "../mock/blog";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useToast } from "../hooks/use-toast";
import { Shield, Zap, Wrench, Lock, AlertTriangle, Server, Gauge, ShieldAlert, Phone, Mail, Globe, CheckCircle2, MessageCircle, Menu, X, BookOpen } from "lucide-react";
import { WHATSAPP_LINK } from "../config/contact";
import QuickContactPopup from "../components/QuickContactPopup";
import SEO from "../components/SEO";
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';
import { submitLead } from "../lib/lead";

const NavLink = ({ href, children, className = "" }) => (
  <a href={href} className={`text-sm font-medium text-foreground/70 hover:text-primary transition-colors ${className}`}>{children}</a>
);

export default function Landing() {
  const { t, i18n } = useTranslation(['common', 'landing']);
  const { toast } = useToast();
  const formRef = useRef(null);
  const [planPrefill, setPlanPrefill] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupPlan, setPopupPlan] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const urlPattern = /^(https?:\/\/)?(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;
  
  // Form validation schema (static for reliability)
  const schema = z.object({
    nombre: z.string().min(2, "Nombre requerido"),
    email: z.string().email("Email inválido"),
    telefono: z.string().min(7, "Teléfono inválido"),
    url: z.string().trim().optional().refine((value) => !value || urlPattern.test(value), "URL inválida"),
    mensaje: z.string().min(5, "Mensaje muy corto"),
    plan: z.string(),
    urgencia: z.string(),
    canal: z.string(),
    consentimiento: z.boolean().refine(v => v, { message: "Consentimiento requerido" })
  });
  
  // Translated services data
  const translatedServices = [
    { id: "optimizacion", icon: "Gauge", key: "optimization" },
    { id: "rescate", icon: "AlertTriangle", key: "rescue" },
    { id: "seguridad", icon: "ShieldAlert", key: "security" },
    { id: "blindaje", icon: "Lock", key: "shield" }
  ];
  
  // Translated problems data
  const translatedProblems = [
    { icon: "AlertTriangle", key: "malware" },
    { icon: "Server", key: "down" },
    { icon: "Gauge", key: "slow" },
    { icon: "ShieldAlert", key: "ransomware" }
  ];
  
  // Translated process steps
  const translatedProcess = [
    { step: 1, key: "step1" },
    { step: 2, key: "step2" },
    { step: 3, key: "step3" },
    { step: 4, key: "step4" }
  ];
  
  // Translated pricing data
  const translatedPricing = [
    { id: "bronce", key: "bronze", price: 99, highlight: false },
    { id: "plata", key: "silver", price: 249, highlight: false },
    { id: "oro", key: "gold", price: 399, highlight: true }
  ];
  
  // Google Analytics helper
  const trackEvent = (eventName, parameters = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, parameters);
    }
    // También enviar a PostHog si está disponible
    if (window.posthog) {
      window.posthog.capture(eventName, parameters);
    }
  };
  const openWhatsApp = (location = "unknown") => {
    trackEvent('cta_click', {
      'event_category': 'conversion',
      'event_label': 'whatsapp_direct',
      location
    });
    window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer");
  };

  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: "",
      email: "",
      telefono: "",
      url: "",
      mensaje: "",
      plan: "rescate",
      urgencia: "consulta",
      canal: "Email",
      consentimiento: false
    }
  });
  
  const consentimiento = watch("consentimiento");

  React.useEffect(() => {
    if (planPrefill) setValue("plan", planPrefill);
  }, [planPrefill, setValue]);

  // Auto-open popup desactivado - concentrándose en formulario del footer
  // React.useEffect(() => {
  //   const already = sessionStorage.getItem("auto_popup_shown");
  //   const target = document.getElementById("problemas");
  //   if (!target || already) return;
  //   const isDesktop = () => {
  //     const wide = window.matchMedia("(min-width: 1024px)").matches;
  //     const notCoarse = !window.matchMedia("(pointer: coarse)").matches;
  //     return wide && notCoarse;
  //   };
  //   if (!isDesktop()) return;
  //   const obs = new IntersectionObserver((entries) => {
  //     entries.forEach((e) => {
  //       if (e.isIntersecting && e.intersectionRatio > 0.25) {
  //         sessionStorage.setItem("auto_popup_shown", "1");
  //         setTimeout(() => setPopupOpen(true), 500);
  //         obs.disconnect();
  //       }
  //     });
  //   }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
  //   obs.observe(target);
  //   return () => obs.disconnect();
  // }, []);

  const onSubmit = async (data) => {
    try {
      const resetForm = () => {
        setValue('nombre', '');
        setValue('email', '');
        setValue('telefono', '');
        setValue('url', '');
        setValue('mensaje', '');
        setValue('consentimiento', false);
        setValue('plan', 'rescate');
        setValue('urgencia', 'consulta');
        setValue('canal', 'Email');
      };

      const trackLead = (method) => {
        trackEvent('form_submit', {
          'event_category': 'contact',
          'event_label': data.plan,
          'plan': data.plan,
          'urgencia': data.urgencia,
          'canal': data.canal,
          'method': method
        });
        trackEvent('lead_generated', {
          'event_category': 'conversion',
          'plan': data.plan,
          'urgencia': data.urgencia,
          'value': 1
        });
      };

      const result = await submitLead(data, 'landing-react');
      if (result.delivered) {
        trackLead(result.method);
        toast({ 
          title: '¡Solicitud enviada!', 
          description: 'Hemos recibido tu solicitud. Te contactaremos pronto.', 
        });
        resetForm();
      } else {
        toast({ 
          title: 'No pudimos enviar tu solicitud', 
          description: 'Por favor contáctanos por WhatsApp para atención inmediata.', 
        });
      }
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (e) {
      toast({
        title: 'Error de conexión', 
        description: 'Por favor contáctanos directamente por WhatsApp.', 
      });
    }
  };

  // Popup desactivado - redirigir al formulario del footer
  const openPopup = (plan) => { 
    // setPopupPlan(plan || ""); 
    // setPopupOpen(true); 
    // En su lugar, hacer scroll al formulario del footer
    if (plan) setPlanPrefill(plan);
    const contactSection = document.getElementById('contacto');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const ServiceIcon = ({ name }) => {
    const map = { AlertTriangle, Server, Gauge, ShieldAlert, Lock, Wrench };
    const Icon = map[name] || AlertTriangle;
    return <Icon className="w-8 h-8 text-primary" />;
  };

  const ProblemIcon = ({ name }) => {
    const map = { AlertTriangle, Server, Gauge, ShieldAlert };
    const Icon = map[name] || AlertTriangle;
    return <Icon className="w-5 h-5 text-destructive" />;
  };

  // Contador animado de sitios rescatados (para STATS)
  const [sitesCount, setSitesCount] = useState(200);
  
  // Calcular sitios rescatados basados en fecha inicial (gradual, máximo 5/mes)
  const calculateSites = useCallback(() => {
    const startDate = new Date('2024-01-01'); // Fecha de inicio con 200 sitios
    const now = new Date();
    const monthsPassed = (now.getFullYear() - startDate.getFullYear()) * 12 + (now.getMonth() - startDate.getMonth());
    
    // Crecimiento gradual: 1-2 sitios por semana (4-5 por mes)
    // Calculamos semanas totales y agregamos 1.5 sitios promedio por semana
    const weeksPassed = Math.floor((now - startDate) / (7 * 24 * 60 * 60 * 1000));
    const sitiosNuevos = Math.floor(weeksPassed * 1.5); // 1.5 sitios/semana = ~6 sitios/mes máx
    
    // Limitamos a máximo 5 sitios por mes para mayor control
    const sitiosMaxPorMes = monthsPassed * 5;
    const sitiosTotales = Math.min(200 + sitiosNuevos, 200 + sitiosMaxPorMes);
    
    return sitiosTotales;
  }, []);
  
  // Animación del contador
  useEffect(() => {
    const targetCount = calculateSites();
    let currentCount = 200;
    const increment = Math.ceil((targetCount - currentCount) / 50);
    const timer = setInterval(() => {
      currentCount += increment;
      if (currentCount >= targetCount) {
        setSitesCount(targetCount);
        clearInterval(timer);
      } else {
        setSitesCount(currentCount);
      }
    }, 30);
    
    return () => clearInterval(timer);
  }, [calculateSites]);

  return (
    <>
      <SEO 
        title="Rescate WordPress 24/7 | Sitio Hackeado o Caído"
        description="Rescatamos sitios WordPress hackeados, caídos o lentos en menos de 24 horas. Especialistas con +200 sitios recuperados. Servicio 24/7 en México de GAREY AW."
        keywords="rescate wordpress, wordpress hackeado, sitio wordpress caído, recuperar wordpress, reparar wordpress méxico, wordpress lento, sitio hackeado"
        canonicalUrl="https://wprescate24.com/"
      />
      <div className="dark min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-sm bg-white/95 border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <RouterLink to="/" className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <span className="font-bold text-foreground text-lg">WPRescate24</span>
          </RouterLink>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            {/* Menú de Servicios */}
            <div className="flex items-center gap-4 xl:gap-6 xl:pr-6 xl:border-r border-border">
              <RouterLink to="/" className="text-sm font-medium text-primary hover:opacity-80 transition-colors">{t('common:nav.home')}</RouterLink>
              <RouterLink to="/mantenimiento-wordpress" className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors">{t('common:nav.maintenance')}</RouterLink>
              <RouterLink to="/optimizacion-velocidad-wordpress" className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors">{t('common:nav.optimization')}</RouterLink>
              <RouterLink to="/hosting-wordpress" className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors">{t('common:nav.hosting')}</RouterLink>
              <RouterLink to="/blog" className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors">{t('common:nav.blog')}</RouterLink>
            </div>
            {/* Navegación de página actual */}
            <NavLink href="#servicios" className="hidden xl:inline">{t('common:nav.services')}</NavLink>
            <NavLink href="#proceso" className="hidden xl:inline">Proceso</NavLink>
            <NavLink href="#pricing" className="hidden xl:inline">{t('common:nav.pricing')}</NavLink>
          </nav>
          
          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="text-sm text-secondary hover:text-secondary/80 font-medium">WhatsApp</a>
            <Button className="bg-primary hover:bg-primary/90 text-white font-semibold" onClick={() => openPopup()}>{t('common:nav.contact')}</Button>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-3">
            <div className="md:hidden"><LanguageSelector /></div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-foreground p-2 hover:bg-primary/5 rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-white">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
              <RouterLink 
                to="/" 
                className="text-sm font-medium text-primary hover:opacity-80 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common:nav.home')}
              </RouterLink>
              <RouterLink 
                to="/mantenimiento-wordpress" 
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common:nav.maintenance')}
              </RouterLink>
              <RouterLink 
                to="/optimizacion-velocidad-wordpress" 
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common:nav.optimization')}
              </RouterLink>
              <RouterLink 
                to="/hosting-wordpress" 
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common:nav.hosting')}
              </RouterLink>
              <RouterLink 
                to="/blog" 
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common:nav.blog')}
              </RouterLink>
              <a 
                href="#servicios" 
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common:nav.services')}
              </a>
              <a 
                href="#proceso" 
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Proceso
              </a>
              <a 
                href="#pricing" 
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common:nav.pricing')}
              </a>
              <a 
                href={WHATSAPP_LINK} 
                target="_blank" 
                rel="noopener" 
                className="text-sm text-secondary hover:text-secondary/80 font-medium py-2"
              >
                WhatsApp
              </a>
              <Button 
                className="bg-primary hover:bg-primary/90 text-white font-semibold mt-2" 
                onClick={() => { 
                  setMobileMenuOpen(false);
                  openPopup();
                }}
              >
                {t('common:nav.contact')}
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{ background: 'radial-gradient(640px 320px at 75% 8%, hsl(68 60% 51% / 0.12), transparent 65%), radial-gradient(520px 280px at 15% 90%, hsl(223 82% 21% / 0.06), transparent 60%)' }} />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/30 mb-6">
              <Zap className="w-4 h-4 text-secondary" />
              <span className="text-sm font-medium text-secondary">{t('landing:hero.subtitle')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
              {t('landing:hero.title')}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-foreground/80 max-w-3xl mx-auto leading-relaxed">
              {t('landing:hero.emergency')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Button className="btn-premium bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 text-base" onClick={() => {
                trackEvent('cta_click', {
                  'event_category': 'engagement',
                  'event_label': 'hero_urgent_support',
                  'location': 'hero'
                });
                openPopup();
              }}>
                🚨 {t('landing:hero.cta')}
              </Button>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/5 font-semibold px-8 py-6 text-base" onClick={() => {
                openWhatsApp("hero_secondary");
              }}>
                {t('landing:hero.secondaryCta')}
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-8 text-sm text-foreground/70">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-secondary" /> 
                <strong>Recuperación</strong> en 24h garantizada
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-secondary" /> 
                <strong>Endurecimiento</strong> de seguridad
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-secondary" /> 
                <strong>Soporte 24/7</strong>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Quick Contact Strip */}
      <section className="py-5 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm font-semibold">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>{t('landing:quickLead.response')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>{t('landing:quickLead.diagnosis')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>{t('landing:quickLead.noCommitment')}</span>
            </div>
          </div>
          <Button className="bg-white text-primary hover:bg-white/90 font-bold" onClick={() => openWhatsApp("quick_strip")}>
            <MessageCircle className="w-4 h-4 mr-2" />
            {t('landing:quickLead.cta')}
          </Button>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-6 text-center hover:border-primary/30 transition-colors">
            <div className="text-3xl font-bold text-primary">
              +{sitesCount}
            </div>
            <div className="text-foreground/70 text-sm mt-1 font-medium">{t('landing:stats.recovered')}</div>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-6 text-center hover:border-primary/30 transition-colors">
            <div className="text-3xl font-bold text-primary">
              24/7
            </div>
            <div className="text-foreground/70 text-sm mt-1 font-medium">{t('landing:stats.support_text')}</div>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-6 text-center hover:border-primary/30 transition-colors">
            <div className="text-3xl font-bold text-primary">
              9h
            </div>
            <div className="text-foreground/70 text-sm mt-1 font-medium">{t('landing:stats.avgTime_text')}</div>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-6 text-center hover:border-primary/30 transition-colors">
            <div className="text-3xl font-bold text-primary">
              98%
            </div>
            <div className="text-foreground/70 text-sm mt-1 font-medium">{t('landing:stats.satisfaction_text')}</div>
          </div>
        </div>
      </section>

      {/* Servicios Principales */}
      <section id="servicios" className="py-20 bg-gradient-to-b from-background to-primary/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('landing:services.title')}</h2>
            <p className="mt-4 text-lg text-foreground/70">{t('landing:services.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {translatedServices.map((service) => (
              <Card key={service.id} className="bg-background border-2 border-border hover:border-primary/50 transition-all hover:shadow-xl group">
                <CardHeader className="space-y-4">
                  <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <ServiceIcon name={service.icon} />
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">{t(`landing:services.${service.key}.title`)}</CardTitle>
                  <p className="text-sm text-foreground/70">{t(`landing:services.${service.key}.desc`)}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {t(`landing:services.${service.key}.features`, { returnObjects: true }).map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                        <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" /> 
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Problemas Urgentes */}
      <section id="problemas" className="py-16 bg-destructive/5 border-y border-destructive/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('landing:problems.title')}</h2>
            <p className="mt-3 text-lg text-foreground/70">{t('landing:problems.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {translatedProblems.map((p) => (
              <Card key={p.key} className="bg-background border-destructive/30 hover:border-destructive/50 transition-all card-hover">
                <CardContent className="p-5 flex items-start gap-3">
                  <ProblemIcon name={p.icon} />
                  <div>
                    <div className="font-bold text-foreground">{t(`landing:problems.${p.key}.title`)}</div>
                    <div className="text-foreground/70 text-sm mt-1">{t(`landing:problems.${p.key}.desc`)}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso de Rescate */}
      <section id="proceso" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('landing:process.title')}</h2>
            <p className="text-lg text-foreground/70 mt-4">{t('landing:process.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {translatedProcess.map((step) => (
              <Card key={step.step} className="bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-border hover:border-primary/50 hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-white text-xl font-bold mb-3">
                    {step.step}
                  </div>
                  <CardTitle className="text-foreground text-lg font-bold">{t(`landing:process.${step.key}.title`)}</CardTitle>
                </CardHeader>
                <CardContent className="text-foreground/70 text-sm leading-relaxed">{t(`landing:process.${step.key}.desc`)}</CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Por qué Elegirnos - Social Proof */}
      <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('landing:socialProof.title')}</h2>
            <p className="text-lg text-foreground/70 mt-4">{t('landing:socialProof.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="inline-flex h-20 w-20 rounded-full bg-secondary/10 items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-secondary" />
              </div>
              <div className="text-4xl font-bold text-primary mb-2">200+</div>
              <div className="text-foreground/70">{t('landing:socialProof.sitesRecovered')}</div>
            </div>
            <div className="text-center">
              <div className="inline-flex h-20 w-20 rounded-full bg-secondary/10 items-center justify-center mb-4">
                <Zap className="w-10 h-10 text-secondary" />
              </div>
              <div className="text-4xl font-bold text-primary mb-2">{'<'} 24h</div>
              <div className="text-foreground/70">{t('landing:socialProof.recoveryTime')}</div>
            </div>
            <div className="text-center">
              <div className="inline-flex h-20 w-20 rounded-full bg-secondary/10 items-center justify-center mb-4">
                <Shield className="w-10 h-10 text-secondary" />
              </div>
              <div className="text-4xl font-bold text-primary mb-2">98%</div>
              <div className="text-foreground/70">{t('landing:socialProof.customerSatisfaction')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('landing:pricing.title')}</h2>
            <p className="text-lg text-foreground/70 mt-4">{t('landing:pricing.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {translatedPricing.map((p) => (
              <Card key={p.id} className={`relative bg-background border-2 ${p.highlight ? "border-primary shadow-xl shadow-primary/10" : "border-border"} overflow-hidden transition-all hover:shadow-2xl`}>
                {p.highlight && (
                  <div className="absolute right-4 top-4 text-xs px-3 py-1 rounded-full bg-secondary text-white font-bold">{t('landing:pricing.mostChosen')}</div>
                )}
                <CardHeader className="space-y-4">
                  <CardTitle className="text-foreground flex items-baseline justify-between">
                    <span className="text-2xl font-bold">{t(`landing:pricing.${p.key}.name`)}</span>
                    <span className="text-primary text-3xl font-bold">${p.price}</span>
                  </CardTitle>
                  <p className="text-sm text-foreground/70">{t(`landing:pricing.${p.key}.description`)}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {t(`landing:pricing.${p.key}.features`, { returnObjects: true }).map((f, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-foreground/80">
                        <CheckCircle2 className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" /> 
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className={`mt-8 w-full font-bold ${p.highlight ? 'bg-primary hover:bg-primary/90' : 'bg-secondary hover:bg-secondary/90'} text-white`} onClick={() => {
                    trackEvent('plan_selected', {
                      'event_category': 'pricing',
                      'event_label': p.id,
                      'plan': p.id,
                      'price': p.price,
                      'location': 'pricing_section'
                    });
                    setPlanPrefill(p.id);
                    openPopup(p.id);
                  }}>{t('landing:pricing.choose')} {t(`landing:pricing.${p.key}.name`)}</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonios" className="py-20 bg-gradient-to-b from-background to-primary/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('landing:testimonials.title')}</h2>
            <p className="text-lg text-foreground/70 mt-4">{t('landing:testimonials.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {['ana', 'carlos', 'lucia'].map((key) => (
              <Card key={key} className="bg-background border-2 border-border hover:border-primary/30 hover:shadow-xl transition-all">
                <CardContent className="pt-6">
                  <p className="text-foreground/90 italic text-base leading-relaxed">"{t(`landing:testimonials.${key}.quote`)}"</p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-secondary/10 border-2 border-secondary/30 text-secondary flex items-center justify-center text-sm font-bold">
                      {t(`landing:testimonials.${key}.name`).split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <div className="text-foreground text-sm font-bold">{t(`landing:testimonials.${key}.name`)}</div>
                      <div className="text-foreground/60 text-xs">{t(`landing:testimonials.${key}.role`)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section id="blog" className="py-20 bg-background border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">{t('landing:blog.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('landing:blog.title')}</h2>
            <p className="text-lg text-foreground/70 mt-4">{t('landing:blog.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {BLOG_POSTS.slice(0, 3).map((post) => (
              <Card key={post.slug} className="bg-background border-2 border-border hover:border-primary/40 transition-all hover:shadow-xl">
                <CardHeader>
                  <p className="text-xs uppercase tracking-wide text-secondary font-semibold">{post.category}</p>
                  <CardTitle className="text-xl text-foreground leading-tight">{post.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-foreground/75 leading-relaxed">{post.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-foreground/60">{post.readTime}</span>
                    <RouterLink to={`/blog/${post.slug}`} className="text-sm font-semibold text-primary hover:opacity-80">
                      {t('landing:blog.readArticle')} →
                    </RouterLink>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button className="bg-primary hover:bg-primary/90 text-white font-bold" asChild>
              <RouterLink to="/blog">{t('landing:blog.viewAll')}</RouterLink>
            </Button>
            <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary/5 font-semibold" onClick={() => openWhatsApp("blog_preview")}>
              <MessageCircle className="w-4 h-4 mr-2" />
              {t('landing:blog.cta')}
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            {t('landing:cta_final.title')}
          </h2>
          <p className="text-lg text-foreground/70 mb-10 max-w-2xl mx-auto">
            {t('landing:cta_final.subtitle')}
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="border-2 border-destructive/50 bg-destructive/5 hover:border-destructive hover:shadow-2xl transition-all">
              <CardContent className="p-8 text-center">
                <AlertTriangle className="w-16 h-16 text-destructive mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">{t('landing:cta_final.emergency.title')}</h3>
                <p className="text-foreground/70 mb-6">{t('landing:cta_final.emergency.desc')}</p>
                <Button className="w-full bg-destructive hover:bg-destructive/90 text-white font-bold py-6 text-base" onClick={() => {
                  trackEvent('cta_click', {
                    'event_category': 'conversion',
                    'event_label': 'final_cta_emergency',
                    'location': 'final_cta'
                  });
                  openPopup();
                }}>
                  {t('landing:cta_final.emergency.button')}
                </Button>
              </CardContent>
            </Card>
            <Card className="border-2 border-secondary/50 bg-secondary/5 hover:border-secondary hover:shadow-2xl transition-all">
              <CardContent className="p-8 text-center">
                <Shield className="w-16 h-16 text-secondary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">{t('landing:cta_final.prevention.title')}</h3>
                <p className="text-foreground/70 mb-6">{t('landing:cta_final.prevention.desc')}</p>
                <Button className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold py-6 text-base" onClick={() => {
                  trackEvent('cta_click', {
                    'event_category': 'conversion',
                    'event_label': 'final_cta_proactive',
                    'location': 'final_cta'
                  });
                  document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                }}>
                  {t('landing:cta_final.prevention.button')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ + Contacto */}
      <section id="faqs" className="py-20 bg-background border-t border-border">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('landing:faq.title')}</h2>
            <p className="text-foreground/70 mt-4 text-lg">{t('landing:faq.subtitle')}</p>
            <div className="mt-8 rounded-xl bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-border p-6">
              <Accordion type="single" collapsible className="w-full">
                {[1, 2, 3, 4].map((i) => (
                  <AccordionItem key={i} value={`item-${i}`}>
                    <AccordionTrigger className="text-left text-foreground font-semibold">{t(`landing:faq.q${i}`)}</AccordionTrigger>
                    <AccordionContent className="text-foreground/70">{t(`landing:faq.a${i}`)}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
          {/* Contact form */}
          <div id="contacto" ref={formRef} className="rounded-xl bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-border p-8">
            <h3 className="text-2xl font-bold text-foreground">{t('common:form.contactUs')}</h3>
            <p className="text-foreground/70 text-sm mt-2">{t('common:form.contactSubtitle')}</p>
            <form className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="col-span-1">
                <Label htmlFor="nombre" className="text-foreground font-medium">{t('common:form.name')}</Label>
                <Input id="nombre" placeholder={t('common:form.namePlaceholder')} {...register("nombre")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.nombre && <p className="text-destructive text-xs mt-1">{errors.nombre.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="email" className="text-foreground font-medium">{t('common:form.email')}</Label>
                <Input id="email" placeholder={t('common:form.emailPlaceholder')} {...register("email")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="telefono" className="text-foreground font-medium">{t('common:form.phone')}</Label>
                <Input id="telefono" placeholder={t('common:form.phonePlaceholder')} {...register("telefono")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.telefono && <p className="text-destructive text-xs mt-1">{errors.telefono.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="url" className="text-foreground font-medium">{t('common:form.siteUrl')}</Label>
                <Input id="url" placeholder={t('common:form.urlPlaceholder')} {...register("url")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.url && <p className="text-destructive text-xs mt-1">{errors.url.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">{t('common:form.serviceInterest')}</Label>
                <Select onValueChange={(v) => setValue("plan", v)} defaultValue="rescate">
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder={t('common:form.choosePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="rescate">{t('common:services.rescue')}</SelectItem>
                    <SelectItem value="mantenimiento">{t('common:services.maintenance')}</SelectItem>
                    <SelectItem value="optimizacion">{t('common:services.optimization')}</SelectItem>
                    <SelectItem value="hosting">{t('common:services.hosting')}</SelectItem>
                  </SelectContent>
                </Select>
                {errors.plan && <p className="text-destructive text-xs mt-1">{errors.plan.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">{t('common:form.urgency')}</Label>
                <Select onValueChange={(v) => setValue("urgencia", v)}>
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder={t('common:form.urgencyPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="4h">{t('common:form.urgencyImmediate')}</SelectItem>
                    <SelectItem value="24h">{t('common:form.urgencyHigh')}</SelectItem>
                    <SelectItem value="48-72h">{t('common:form.urgencyMedium')}</SelectItem>
                  </SelectContent>
                </Select>
                {errors.urgencia && <p className="text-destructive text-xs mt-1">{errors.urgencia.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">{t('common:form.preferredChannel')}</Label>
                <Select onValueChange={(v) => setValue("canal", v)}>
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder={t('common:form.channelPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="Email">{t('common:form.channelEmail')}</SelectItem>
                    <SelectItem value="WhatsApp">{t('common:form.channelWhatsApp')}</SelectItem>
                    <SelectItem value="Llamada">{t('common:form.channelCall')}</SelectItem>
                  </SelectContent>
                </Select>
                {errors.canal && <p className="text-destructive text-xs mt-1">{errors.canal.message}</p>}
              </div>
              <div className="md:col-span-2 col-span-1">
                <Label htmlFor="mensaje" className="text-foreground font-medium">{t('common:form.message')}</Label>
                <Textarea id="mensaje" rows={4} placeholder={t('common:form.messagePlaceholder')} {...register("mensaje")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.mensaje && <p className="text-destructive text-xs mt-1">{errors.mensaje.message}</p>}
              </div>
              <div className="md:col-span-2 col-span-1 flex items-start gap-2">
                <Checkbox 
                  id="consentimiento" 
                  checked={consentimiento}
                  onCheckedChange={(checked) => setValue("consentimiento", checked)}
                  className="mt-1"
                />
                <Label htmlFor="consentimiento" className="text-sm text-foreground/70 cursor-pointer leading-tight">{t('common:form.consent')}</Label>
              </div>
              {errors.consentimiento && <p className="md:col-span-2 col-span-1 text-destructive text-xs -mt-2">{errors.consentimiento.message}</p>}
              <div className="md:col-span-2 col-span-1">
                <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-white font-bold">
                  {isSubmitting ? t('common:form.sending') : t('common:form.submitButton')}
                </Button>
                <p className="mt-3 text-xs text-foreground/70 text-center">{t('landing:contactMicrocopy')}</p>
              </div>
            </form>
            <div className="mt-4 text-xs text-foreground/60 flex items-center gap-4">
              <div className="flex items-center gap-1"><Phone className="w-4 h-4" /> {t('common:form.support247')}</div>
              <div className="flex items-center gap-1"><Mail className="w-4 h-4" /> {t('common:form.fastResponse')}</div>
              <div className="flex items-center gap-1"><Globe className="w-4 h-4" /> {t('common:form.intCoverage')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-[#b8cd37]" />
                <span className="font-bold text-white text-lg">WPRescate24</span>
              </div>
              <p className="text-sm text-white/70">
                {t('common:footer.description')}
              </p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">{t('common:footer.quickLinks')}</h4>
              <div className="flex flex-col gap-2">
                <a href="#servicios" className="text-sm text-white/70 hover:text-white transition-colors">{t('common:footer.services')}</a>
                <a href="#proceso" className="text-sm text-white/70 hover:text-white transition-colors">{t('common:footer.process')}</a>
                <a href="#pricing" className="text-sm text-white/70 hover:text-white transition-colors">{t('common:footer.pricing')}</a>
                <a href="#faqs" className="text-sm text-white/70 hover:text-white transition-colors">{t('common:footer.faq')}</a>
                <RouterLink to="/blog" className="text-sm text-white/70 hover:text-white transition-colors">{t('common:footer.blog')}</RouterLink>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">{t('common:footer.infrastructure')}</h4>
              <p className="text-sm text-white/70 mb-2">{t('common:footer.backedBy')}</p>
              <a 
                href="https://servidores.garey.mx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#b8cd37] hover:text-[#c6da45] transition-colors"
              >
                <Server className="w-4 h-4" />
                servidores.garey.mx
              </a>
            </div>
          </div>
          <div className="border-t border-white/15 mt-8 pt-6 flex flex-col md:flex-row items-center justify-between text-sm text-white/60">
            <div>© {new Date().getFullYear()} WPRescate24. {t('common:footer.allRights')} <a href="https://garey.mx" target="_blank" rel="noopener noreferrer" className="text-white hover:underline font-semibold">GAREY AW</a></div>
            <div className="flex items-center gap-6 mt-3 md:mt-0">
              <RouterLink to="/terminos" className="hover:text-white transition-colors">{t('common:footer.terms')}</RouterLink>
              <RouterLink to="/privacidad" className="hover:text-white transition-colors">{t('common:footer.privacy')}</RouterLink>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="hover:text-[#b8cd37] transition-colors">WhatsApp</a>
            </div>
          </div>
        </div>
      </footer>
      {/* Floating WhatsApp CTA */}
      <button
        type="button"
        onClick={() => openWhatsApp("floating_cta")}
        className="fixed bottom-6 right-6 z-40 rounded-full bg-[#25D366] text-white shadow-2xl px-4 py-3 font-bold text-sm hover:bg-[#20bd5a] transition-all flex items-center gap-2"
        aria-label={t('landing:floatingWhatsApp')}
      >
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline">{t('landing:floatingWhatsApp')}</span>
      </button>

      {/* Quick popup */}
      <QuickContactPopup open={popupOpen} onOpenChange={setPopupOpen} prefillPlan={popupPlan} onSubmitted={() => {}} />
      </div>
    </>
  );
}
